import { useState, memo } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import FormControl from '@zenbusiness/zui/FormControl';
import TextField from '@zenbusiness/zui/TextField';
import PropTypes from 'prop-types';
import { v1 as uuidv1 } from 'uuid';

import AutoCompleteInput from '#client/components/AutoCompleteInput';

import { StyledPopper } from './components/IndustryUIFieldVerticalList.styles';

import { INDUSTRY_CATEGORY_LIST, INDUSTRY_SUBCATEGORY_MAP } from './constants/verticalList';
import { isOptionEqualToValue, renderOption } from './utils/verticalListOptions';

// Find the category and subcategory option objects that correspond to a saved
// businessIndustry value (which is a subcategory enum). Returns { category: null,
// subcategory: null } when nothing matches so the dropdowns start empty.
const findIndustryOptions = (businessIndustryValue) => {
  if (!businessIndustryValue) {
    return { category: null, subcategory: null };
  }
  for (const [categoryValue, subcategories] of Object.entries(INDUSTRY_SUBCATEGORY_MAP)) {
    const subcategory = subcategories.find((s) => s.value === businessIndustryValue);
    if (subcategory) {
      const category = INDUSTRY_CATEGORY_LIST.find((c) => c.value === categoryValue) || null;
      return { category, subcategory };
    }
  }
  return { category: null, subcategory: null };
};

const IndustryUIFieldVerticalList = ({
  field = { setSaved: () => {}, setLoading: () => {} },
  dataChangeHandler = () => {},
  disabled,
  data
}) => {
  const [selectedCategoryOption, setSelectedCategoryOption] = useState(
    () => findIndustryOptions(data?.businessIndustry).category
  );
  const [selectedOption, setSelectedOption] = useState(
    () => findIndustryOptions(data?.businessIndustry).subcategory
  );
  const trackIndustryChangeClick = useButtonPressedCallback({
    elementText: 'business_industry_list',
    elementName: 'banking_collect_info',
    appPageName: 'banking_application'
  });

  const onDataChanged = (option) => {
    if (option) {
      trackIndustryChangeClick();
      field.setLoading();
    }
    dataChangeHandler(
      !!option,
      { businessIndustry: option?.value },
      (forceUpdate, { isSave, isError }) => {
        field.setSaved(isSave, isError);
        forceUpdate();
      },
      field.key
    );
  };

  const handleChangeOption = (event, option) => {
    setSelectedOption(option);
    onDataChanged(option);
  };

  const handleCategoryChangeOption = (event, option) => {
    setSelectedCategoryOption(option);
    setSelectedOption(null);
    onDataChanged(null);
  };

  return (
    <FormControl
      fullWidth
      sx={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 3 }}
      variant="outlined"
    >
      <AutoCompleteInput
        autoHighlight
        blurOnSelect
        disabled={disabled}
        getOptionLabel={(option) => option.toString()}
        isOptionEqualToValue={isOptionEqualToValue}
        ListboxProps={{
          style: {
            maxHeight: '400px',
            padding: '16px 0px',
            borderRadius: '16px'
          }
        }}
        noOptionsText="No results. Try a new keyword."
        onChange={handleCategoryChangeOption}
        openOnFocus
        options={INDUSTRY_CATEGORY_LIST}
        PopperComponent={StyledPopper}
        renderInput={(params) => (
          <TextField
            {...params}
            inputProps={{
              ...params.inputProps,
              // Use for preventing auto suggestion from browser
              name: uuidv1()
            }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            InputProps={{
              ...params.InputProps
            }}
            label="Select industry type"
          />
        )}
        renderOption={renderOption}
        value={selectedCategoryOption || null}
      />

      {selectedCategoryOption?.value && (
        <AutoCompleteInput
          autoHighlight
          blurOnSelect
          disabled={disabled}
          getOptionLabel={(option) => option.toString()}
          isOptionEqualToValue={isOptionEqualToValue}
          ListboxProps={{
            style: {
              maxHeight: '400px',
              padding: '16px 0px',
              borderRadius: '16px'
            }
          }}
          noOptionsText="No results. Try a new keyword."
          onChange={handleChangeOption}
          openOnFocus
          options={INDUSTRY_SUBCATEGORY_MAP[selectedCategoryOption?.value]}
          PopperComponent={StyledPopper}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                // Use for preventing auto suggestion from browser
                name: uuidv1()
              }}
              // eslint-disable-next-line react/jsx-no-duplicate-props
              InputProps={{
                ...params.InputProps
              }}
              label="Select industry sub-type"
            />
          )}
          renderOption={renderOption}
          value={selectedOption || null}
        />
      )}
    </FormControl>
  );
};

IndustryUIFieldVerticalList.propTypes = {
  data: PropTypes.shape({
    businessIndustry: PropTypes.string
  }),
  dataChangeHandler: PropTypes.func,
  field: PropTypes.shape({
    setSaved: PropTypes.func,
    setLoading: PropTypes.func,
    key: PropTypes.string
  }),
  disabled: PropTypes.bool,
  useFor: PropTypes.string
};

function areEqual(preProp, nextProp) {
  return (
    preProp.field === nextProp.field &&
    preProp.data === nextProp.data &&
    preProp.disabled === nextProp.disabled
  );
}

export default memo(IndustryUIFieldVerticalList, areEqual);
